import clsx from 'clsx'
import React, { FunctionComponent, useLayoutEffect, useState } from 'react'

import { Icon } from '../Icon'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Span } from '../Typography'
import { getInitials } from '../utils/string'
import { SIZES } from './constants'
import styles from './style.module.scss'

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type AvatarSizes = SizesType[SizesKeys]

export interface AvatarProps {
  src?: string
  alt: string
  name: string
  size?: AvatarSizes
  title?: string
  testNameSpace?: string
}

export type AvatarComponent = FunctionComponent<AvatarProps>

function useImageSourceIsValid(src: string | undefined): boolean {
  const [valid, setValid] = useState(false)

  useLayoutEffect(() => {
    if (typeof src === 'undefined') {
      return
    }

    setValid(false)
    const image: HTMLImageElement = new Image()
    image.src = src
    image.onload = () => {
      setValid(true)
    }
    image.onerror = () => {
      setValid(false)
    }
  }, [src])

  return valid
}

export const Avatar: AvatarComponent = ({ size = SIZES.M, src, alt, name, title, testNameSpace = '', ...otherProps }) => {
  const themeClass = useThemeClass()
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)

  const imageClasses = clsx(styles[`size${size}`], styles.img, styles[themeClass])
  const fallbackAvatarStyles = clsx(styles.text, styles[`size${size}`], styles[themeClass])
  const fallbackLogoStyles = clsx(styles.logoFallback, styles[`size${size}`], styles[themeClass])
  const hasTestId = testNameSpace.length > 0
  const nameIsEmpty = name.trim() === ''

  const renderFallbackText = (): JSX.Element => {
    const eligibleSizes: AvatarSizes[] = [SIZES.S, SIZES.M, SIZES.L, SIZES.XL]
    const eligibleForLogoFallback = eligibleSizes.includes(size)

    if (nameIsEmpty && eligibleForLogoFallback) {
      const iconName = isBronteDesign ? 'Afterpay' : 'Profile'
      const iconSizes = size === SIZES.S ? 'Small' : 'Medium'
      return (
        <div className={fallbackLogoStyles}>
          <Icon name={iconName} size={iconSizes} title={title} />
        </div>
      )
    }

    const avatarText = isBronteDesign ? getInitials(name) : name.charAt(0).toUpperCase()
    return (
      <span className={fallbackAvatarStyles} {...(hasTestId && { 'data-testid': `${testNameSpace}-fallbackText` })}>
        <Span bold={!isBronteDesign}>{avatarText}</Span>
      </span>
    )
  }

  const renderAvatarImage = (): JSX.Element => (
    <img className={imageClasses} title={title} src={src} alt={alt} {...(hasTestId && { 'data-testid': `${testNameSpace}-image` })} {...otherProps} />
  )

  return useImageSourceIsValid(src) ? renderAvatarImage() : renderFallbackText()
}
